import { FunctionInterpolation, Theme } from '@emotion/react';
import React, { useId } from 'react';
import { Alert, AlertProps, AlertTitle } from '@mui/material';

import {
  inline_notification_base,
  warning_inline_notification,
  error_inline_notification,
  success_inline_notification,
  info_inline_notification,
  white_inline_notification,
  subtle_inline_notification,
} from './InlineNotification.styles';
import { TTypographyProps, Typography } from '../Typography';
import { Icon } from '../Icon';
import { RemoveMuiProps } from '../types';
import { IconName } from '../Icon/types';
import { IconButton } from '../IconButton';
import { Button } from '../Button/Button';

type TInlineNotificationColor = 'warning' | 'error' | 'success' | 'info' | 'white' | 'subtle';

export interface TInlineNotificationButtonCta {
  text: string;
  onClick: VoidFunction;
  loading?: boolean;
}

export type TInlineNotificationProps = RemoveMuiProps<
  AlertProps,
  'variant' | 'color' | 'icon' | 'severity' | 'iconMapping'
> & {
  title?: string;
  content: React.ReactElement<TTypographyProps<'p', 'body1'>> | string;
  variant: TInlineNotificationColor;
  /**
   * Send name = '' to remove the icon
   */
  iconConfig?: {
    name: IconName | '';
  };
  buttonCtaConfig?: TInlineNotificationButtonCta[];
  hideLeftBorder?: boolean;
};

const colorConfigClassesMap: Record<TInlineNotificationColor, FunctionInterpolation<Theme>> = {
  warning: warning_inline_notification,
  error: error_inline_notification,
  success: success_inline_notification,
  info: info_inline_notification,
  white: white_inline_notification,
  subtle: subtle_inline_notification,
};

const colorConfigIconMap: Record<TInlineNotificationColor, IconName> = {
  warning: 'WarningFill',
  error: 'CloseCrossCircleFill',
  success: 'CheckCircleFill',
  info: 'InfoCircleFill',
  white: 'InfoCircleFill',
  subtle: 'InfoCircleFill',
};

/**
 *
 * This component controls the render logic for actions in the notification
 */
const InlineNotificationCta: React.FC<Pick<TInlineNotificationProps, 'action' | 'buttonCtaConfig' | 'onClose'>> = (
  props,
) => {
  const { action, buttonCtaConfig, onClose } = props;

  if (!buttonCtaConfig && !action && !onClose) {
    return null;
  }

  /**
   * Button actions are prioritized over custom actions
   */
  const shouldShowCustomAction = action && !buttonCtaConfig;

  return (
    <div className="BlocksInlineNotification-actionContainer">
      {buttonCtaConfig &&
        buttonCtaConfig?.map((buttonConfig) => (
          <Button
            size="small"
            variant="secondary"
            key={buttonConfig.text}
            onClick={buttonConfig.onClick}
            loading={buttonConfig.loading}
          >
            {buttonConfig.text}
          </Button>
        ))}

      {shouldShowCustomAction && action}

      {onClose && (
        <IconButton
          icon="CloseCross"
          size="small"
          onClick={onClose}
          className="BlocksInlineNotification-actionButton"
          tooltip="Close"
        />
      )}
    </div>
  );
};

export const InlineNotification: React.FC<TInlineNotificationProps> = ({
  variant,
  content,
  title,
  iconConfig = undefined,
  hideLeftBorder,
  ...restProps
}: TInlineNotificationProps) => {
  const notificationId = useId();
  const name = iconConfig ? iconConfig.name : colorConfigIconMap[variant];

  // If notification has a close callback or notification suffix
  // Button Icons can be changed if need be
  // Action is react node, should include typography and icon with tooltip only
  if ('onClose' in restProps || 'action' in restProps || 'buttonCtaConfig' in restProps) {
    const { buttonCtaConfig, action = undefined, onClose, ...commonProps } = restProps;

    return (
      <Alert
        variant="outlined"
        css={[inline_notification_base, colorConfigClassesMap[variant]]}
        data-testid="blocks-inline-notification"
        aria-describedby={notificationId}
        icon={!!name && <Icon name={name} />}
        action={<InlineNotificationCta action={action} buttonCtaConfig={buttonCtaConfig} onClose={onClose} />}
        {...commonProps}
      >
        {!hideLeftBorder && (
          <div className="BlocksAlert-left-border" data-testid="blocks-inline-notification-left-border" />
        )}
        {title && <AlertTitle>{title}</AlertTitle>}
        <Typography variant="body2" id={notificationId}>
          {content}
        </Typography>
      </Alert>
    );
  }

  return (
    <Alert
      variant="outlined"
      css={[inline_notification_base, colorConfigClassesMap[variant]]}
      data-testid="blocks-inline-notification"
      aria-describedby={notificationId}
      icon={!!name && <Icon name={name} />}
      {...restProps}
    >
      {!hideLeftBorder && (
        <div className="BlocksAlert-left-border" data-testid="blocks-inline-notification-left-border" />
      )}
      {title && <AlertTitle>{title}</AlertTitle>}
      <Typography variant="body2" id={notificationId}>
        {content}
      </Typography>
    </Alert>
  );
};
